import { Component, OnInit, OnChanges, Output, EventEmitter, ViewChild, Input } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SessionStorageService } from 'angular-web-storage';
import { ClaimsSettingsService } from '../../../services/settings/settings.service';
import { QuestionSetService, LayoutService, Question, BaseQuestionLogicComponent, PagingService, 
  SharedSettingsService } from 'wtw-ui-components';
import { ClaimDetails, SidebarObject, UploadDocuments } from '../../models';
import moment from 'moment';


const pageLabels = {
  editLabel: 'Shared.lblEdit',
  policyDetailsHeadingLabel: 'Shared.lblPolicyDetailsHeading',
  policyNumberLabel: 'Shared.lblPolicyNumber',
  policyInceptionDateLabel: 'Shared.lbPolicyInceptionDate',
  policyExpiryDateLabel: 'Shared.lblPolicyExpiryDate',
  policyTermLabel: 'Shared.lblPolicyTerm',
  policyPremiumLabel: 'Shared.lblPolicyPremium',
  startClaimLabel: 'Claim.lblNewClaimStartClaim',
  lossDateLabel: 'Claim.lblLossDate',
  lossTimeLabel: 'Claim.lblLossTime',
  timeZoneLabel: 'Claim.lblTimeZone',
  claimReasonLabel: 'Claim.lblClaimReason',
  claimDetailsLabel: 'Claim.lblClaimDetails',
  awareDateLabel: 'Claim.lblAwareDate',
  previousAnswersLabel: 'Shared.lblPreviousAnswers',
  supportingDocumentsLabel: 'Claim.lblNewClaimSupportingDocuments',
  claimQuestionnaireLabel: 'Claim.lblNewClaimQuestionnaire',
  claimUploadedDocumentsLabel: 'Claim.lblNewClaimUploadedDocuments'

};

@Component({
  selector: 'app-question-container',
  templateUrl: './question-container.component.html',
  styleUrls: ['./question-container.component.scss'],
})
export class QuestionContainerComponent implements OnInit, OnChanges {
  // @Output() clicked = new EventEmitter<Question>();
  isMobile = false;
  isTablet = false;
  isDesktop = false;
  isSkeletalPolicy = false;
  isEnableAssently = false;
  policyData: SidebarObject = new SidebarObject();

  claimDataObject: SidebarObject;
  uploadDocumentsObject: SidebarObject;
  allowCustomerIdentification: any;
  labels = {
    edit: '',
    policyDetailsHeading: '',
    policyNumber: '',
    policyInceptionDate: '',
    policyExpiryDate: '',
    policyTerm: '',
    policyPremium: '',
    startClaim: '',
    lossDate: '',
    lossTime: '',
    timeZone: '',
    claimReason: '',
    claimDetails: '',
    awareDate: '',
    previousAnswers: '',
    supportingDocuments: '',
    uploadedDocuments: ''
  };

  private sponsorId: number;
  private claimQuestionnaireLabel: string;
  @Output() changeClaimQuestion = new EventEmitter();
  @Input() refreshRequired = false;

  constructor(
    private questionSetService: QuestionSetService,
    private layoutService: LayoutService,
    private translate: TranslateService,
    private sessionStorage: SessionStorageService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private settingsService: ClaimsSettingsService,
    private readonly pagingService: PagingService,
    private sharedSettings: SharedSettingsService
  ) { }

  async ngOnInit() {
    this.sponsorId = this.sessionStorage.get('SponsorId');   
    this.applyTranslations();
    this.loadPreviousAnswers();
    this.checkDeviceWidth();
    const enableCustomerIdentity = sessionStorage.getItem('EnableCustomerIdentityB2CClaims');
    const manageClaimsPortalEnabled = sessionStorage.getItem('ManageClaimsb2cSalesPortalEnabled');
    const enableAssently = sessionStorage.getItem('EnableAssently');
    
    this.isSkeletalPolicy = 
        enableCustomerIdentity && 
        manageClaimsPortalEnabled &&
        !this.allowCustomerIdentification &&
        !JSON.parse(enableCustomerIdentity)._value &&
        JSON.parse(manageClaimsPortalEnabled)._value;
    
    this.isEnableAssently = enableAssently ? JSON.parse(enableAssently)._value : false;
    this.allowCustomerIdentification = this.sharedSettings.allowCustomerIdentification
  }

  ngOnChanges() {
    this.loadPreviousAnswers();
  }

  private loadPreviousAnswers() {
    this.populatePolicyDetails();
    this.populateBasicClaimDetails();
    this.populateUploadDocuments();
  }

  private applyTranslations() {
    const { editLabel, policyDetailsHeadingLabel, policyNumberLabel, policyInceptionDateLabel,
            policyExpiryDateLabel, policyTermLabel, policyPremiumLabel, startClaimLabel, lossDateLabel, lossTimeLabel,
            timeZoneLabel, claimReasonLabel, claimDetailsLabel, awareDateLabel, previousAnswersLabel,
            claimQuestionnaireLabel, supportingDocumentsLabel, claimUploadedDocumentsLabel } = pageLabels;
    this.translate.get([editLabel, policyDetailsHeadingLabel, policyNumberLabel, policyInceptionDateLabel,
      policyExpiryDateLabel, policyTermLabel, policyPremiumLabel, startClaimLabel, lossDateLabel, lossTimeLabel,
      timeZoneLabel, claimReasonLabel, claimDetailsLabel, awareDateLabel, previousAnswersLabel,
      claimQuestionnaireLabel, supportingDocumentsLabel, claimUploadedDocumentsLabel]).subscribe((res) => {
        this.labels.edit = res[editLabel];
        this.labels.policyDetailsHeading = res[policyDetailsHeadingLabel] ?? 'policyDetailsHeadingLabel'; // TEMP!: Remove failovers!
        this.labels.policyNumber = res[policyNumberLabel] ?? 'policyNumberLabel';
        this.labels.policyInceptionDate = res[policyInceptionDateLabel] ?? 'policyInceptionDateLabel';
        this.labels.policyExpiryDate = res[policyExpiryDateLabel] ?? 'policyExpiryDateLabel';
        this.labels.policyTerm = res[policyTermLabel] ?? 'policyTermLabel';
        this.labels.policyPremium = res[policyPremiumLabel] ?? 'policyPremiumLabel';
        this.labels.startClaim = res[startClaimLabel] ?? 'startClaimLabel';
        this.labels.lossDate = res[lossDateLabel] ?? 'lossDateLabel';
        this.labels.lossTime = res[lossTimeLabel] ?? 'lossTimeLabel';
        this.labels.timeZone = res[timeZoneLabel] ?? 'timeZoneLabel';
        this.labels.claimReason = res[claimReasonLabel] ?? 'claimReasonLabel';
        this.labels.claimDetails = res[claimDetailsLabel] ?? 'claimDetailsLabel';
        this.labels.awareDate = res[awareDateLabel] ?? 'awareDateLabel';
        this.labels.previousAnswers = res[previousAnswersLabel] ?? 'previousAnswersLabel';
        this.labels.supportingDocuments = res[supportingDocumentsLabel] ?? 'supportingDocumentsLabel';
        this.claimQuestionnaireLabel = res[claimQuestionnaireLabel] ?? 'claimQuestionnaireLabel';
        this.labels.uploadedDocuments = res[claimUploadedDocumentsLabel] ?? 'claimUploadedDocumentsLabel';

      });
  }

  populatePolicyDetails() {
    const policy = this.settingsService.selectedPolicy;

    if (policy?.policyNumber) {
      this.policyData = new SidebarObject();
      this.policyData = {
        label: this.labels.policyDetailsHeading,
        labels: [
          this.labels.policyNumber,
          this.labels.policyInceptionDate,
          this.labels.policyExpiryDate,
          this.labels.policyTerm,
          this.labels.policyPremium
        ],
        details: [
          policy.policyNumber,
          this.pagingService.convertDateIntoUTC(policy.policyInceptionDate),
          this.pagingService.convertDateIntoUTC(policy.policyExpiryDate),       
          policy.policyTerm,
          policy.premium
        ]
      };
    }
    if (policy?.isContinuous) {

      this.policyData?.details?.splice(2, 1);
      this.policyData?.labels?.splice(2, 1);
      this.policyData?.details?.splice(3, 1);
      this.policyData?.labels?.splice(3, 1);

    }
    return this.policyData;
  }

  populateBasicClaimDetails() {
    const claimDetails: ClaimDetails = this.settingsService.claimDetails;
    if (claimDetails) {
      this.claimDataObject = new SidebarObject();
      this.claimDataObject = {
        label: this.labels.startClaim,
        labels: [
          this.labels.lossDate,
          this.labels.lossTime,
          this.labels.timeZone,
          this.labels.claimReason,
          this.labels.claimDetails,
          this.labels.awareDate
        ],
        details: [
          claimDetails.lossDate,
          claimDetails.lossTime,
          claimDetails.timeZone,
          claimDetails.claimReason,
          claimDetails.claimDetails,
          claimDetails.awareDate
        ]
      };
    }
  }

  populateUploadDocuments() {
    this.uploadDocumentsObject = null;
    if (this.settingsService.uploadDocuments) {
      const uploadDocuments: UploadDocuments = this.settingsService.uploadDocuments;
      this.uploadDocumentsObject = new SidebarObject();
      this.uploadDocumentsObject = {
        label: this.labels.uploadedDocuments,
        labels: [this.labels.supportingDocuments],
        details: [
          uploadDocuments.uploadedFilesName
        ]
      };
    }
  }

  checkDeviceWidth() {
    const layoutChanges$ = this.layoutService?.subscribeToLayoutChanges();
    if (layoutChanges$) {
      layoutChanges$.subscribe(observerResponse => {
        this.isMobile = this.layoutService?.isSmallView() ?? false;
        this.isTablet = this.layoutService?.isMediumView() ?? false;
        this.isDesktop = this.layoutService?.isLargeView() ?? false;
      });
    } else {
      console.warn('layoutService or subscribeToLayoutChanges is undefined.');
    }
  }

  editClicked(question: Question) {
    if (this.router.url === '/claim/claim-questionnaire') {
      this.changeClaimQuestion.emit(question);
    } else {
      this.questionSetService.navigateToIntentCode(question.qIntentCode);
      if (question && question.qQuestionType === 'Notification') {
        this.goToClaimQuestions();
      }
    }
  }

  goToClaimQuestions() {
    this.router.navigate(['claim/claim-questionnaire']);
  }

  goToPolicySelection() {
    if (!this.sharedSettings.allowCustomerIdentification) {
      this.router.navigate(['/account/policy']);
    }
    else {
      this.router.navigate(['/home/new-claim']);  
    }
  }

  goToBasicClaimDetails() {
    this.router.navigate(['/claim/claim-details']);
  }

  goToUploadDocuments() {
    this.router.navigate(['/claim/upload-documents']);
  }
}
